import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import './index.scss'
import FlotiMap from "../../components/FlotiMap"
import { useSidebar } from "../../hooks/useSidebar"
import AreaMonitoring_Sidebar from "./sidebar"
import { initial_sidebar } from "../../contexts/SidebarContext/reducer"
import { useQuery } from "react-query"
import { getAreaMonitoring, getFleetArea } from "../../services/calls/area"
import { stringToPoint, stringToPolygon } from "../../services/utils/convert"
import { Circle, Polygon } from "../../components/FlotiMap/context/types"
import { Draw, FlotiMapRef } from "../../components/FlotiMap/type"

const AreaMonitoring = () => {
  const sidebarRef = useRef()
  const mapRef = useRef<FlotiMapRef>()
  const { updateSidebar } = useSidebar()

  const [visibility, setVisibility] = useState<Record<number, boolean>>({})

  const { data, isLoading, refetch } = useQuery('getAreaMonitoring', () => getAreaMonitoring())
  const { data: areaMon, refetch: refetchFleetArea } = useQuery('getFleetArea', () => getFleetArea())

  useEffect(() => {
    if (sidebarRef?.current) sidebarRef?.current?.setIsLoading(isLoading)
  }, [isLoading, sidebarRef?.current])

  const areaMonLookup = useMemo(() => {
    const lookup: Record<number, number[]> = {}
    if (areaMon) {
      areaMon.forEach((mon) => {
        if (!lookup[mon.areaid]) lookup[mon.areaid] = []
        lookup[mon.areaid].push(mon.fleetid)
      })
    }
    return lookup
  }, [areaMon])

  const handleData = useCallback((sendAreas: boolean = false) => {
    if (data && areaMon) {
      const areas = sidebarRef?.current?.getAreas()
      if (sendAreas || !areas?.length) {
        const mappedData = data.reduce((arr, d) => {
          const newData = { ...d, fleets: areaMonLookup[d.areaid] }
          arr.push(newData)
          return arr
        }, [])
        sidebarRef?.current?.setAreas(mappedData)
      }

      const forms = data.reduce((obj, val) => {
        const isVisible = visibility[val.areaid] ?? true
        if (isVisible) {
          const isCircle = val.radius
          if (isCircle) {
            const { lat, lon } = stringToPoint(val.geom)
            const tempCircle: Circle = {
              lat,
              lon,
              radius: val.radius,
              label: val.name,
              id: val.areaid
            }
            obj.circles.push(tempCircle)
          } else { // then polygon
            const coords = stringToPolygon(val.geom)
            const tempPoly: Polygon = {
              label: val.name,
              id: val.areaid,
              color: 'rgba(216, 31, 40, 0.3)',
              polyshape: coords,
              stroke: {
                color: 'rgba(2, 91, 156, 1)',
                width: 1
              }
            }
            obj.polygons.push(tempPoly)
          }
        }
        return obj
      }, { circles: [], polygons: []})

      mapRef?.current?.setCircles(forms.circles)
      mapRef?.current?.setPolyshapes(forms.polygons)
    }
  }, [data, visibility, areaMonLookup])

  const onAreaChange = useCallback(() => {
    refetch()
    refetchFleetArea()
    handleData(true)
  }, [refetch, refetchFleetArea])

  useEffect(() => {
    updateSidebar({
      content: (
        <AreaMonitoring_Sidebar
          ref={sidebarRef}
          mapRef={mapRef}
          handleVisibility={(vis) => setVisibility(vis)}
          handleDrawing={(draw) => {
            mapRef.current?.enableDrawing(!!draw, draw)
          }}
          onAreaChange={() => onAreaChange()}
        />),
      isOpen: true,
      width: 600
    })

    return () => {
      updateSidebar(initial_sidebar)
    }
  }, [])

  useEffect(() => {
    handleData(false)
  }, [visibility])

  useEffect(() => {
    handleData(true)
  }, [data, areaMon])
  
  return (
    <div className="AreaMonitoring">
      <FlotiMap
        ref={mapRef}
        drawCallback={(val: Draw) => sidebarRef.current.setDraw(val)}
      />
    </div>
  )
}

export default AreaMonitoring
